import React, {
  useState,
  useRef,
  ChangeEvent,
  KeyboardEvent,
  ClipboardEvent,
  useEffect,
} from 'react';

interface OTPInputProps {
  length: number;
  value: string[];
  onChange: (value: string[]) => void;
  onComplete?: (otp: string) => void;
  disabled?: boolean;
}

const OTPInput: React.FC<OTPInputProps> = ({
  length,
  value,
  onChange,
  onComplete,
  disabled = false,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Focus the first empty input box on component mount or value change
  useEffect(() => {
    const firstEmptyIndex = value.findIndex((char) => char === '');
    if (firstEmptyIndex !== -1 && !disabled) {
      inputRefs.current[firstEmptyIndex]?.focus();
    }
  }, [value, disabled]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    if (disabled) return;

    const rawValue = e.target.value;
    const newValue = rawValue.replace(/\D/g, '').slice(-1);

    const newOtp = [...value];
    newOtp[index] = newValue;
    onChange(newOtp);

    if (newValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newOtp.join('').length === length && onComplete) {
      onComplete(newOtp.join(''));
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (disabled) return;

    if (e.key === 'Backspace' && index > 0 && !value[index]) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    e.preventDefault();
    const pasteData = e.clipboardData.getData('text/plain');
    const pastedOtp = pasteData.trim().slice(0, length);

    if (/^\d+$/.test(pastedOtp)) {
      const newOtp = pastedOtp.split('');
      const paddedOtp = newOtp.concat(new Array(length - newOtp.length).fill(''));
      onChange(paddedOtp);

      if (pastedOtp.length === length && onComplete) {
        onComplete(pastedOtp);
      }

      inputRefs.current[length - 1]?.focus();
    }
  };

  const handleClick = (index: number) => {
    if (disabled) return;

    const firstEmptyIndex = value.findIndex((char) => char === '');
    const focusIndex = firstEmptyIndex !== -1 ? firstEmptyIndex : length - 1;
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className="flex space-x-2 justify-center" onPaste={handlePaste}>
      {value.map((char, index) => (
        <input
          key={index}
          type="text"
          maxLength={2}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          className="w-12 h-12 text-center text-xl border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 font-semibold"
          value={char ? '*' : ''}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onClick={() => handleClick(index)}
          ref={(el) => (inputRefs.current[index] = el)}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

export default OTPInput;
